// React
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// VDS-React
import Alert from '@visa/vds-react/alert-styled';
import Icon from '@visa/vds-react/icon-styled';
import AlertIcon from '@visa/vds-react/alert-icon-styled';
import AlertLabel from '@visa/vds-react/alert-label-styled';

// CYBS-UI
import CybsButton from '@cybs-ui/components/lib/button';
import CybsButtonGroup from '@cybs-ui/components/lib/button-group';
import { StructFlex } from '@cybs-ui/components/lib/struct-flex';

import styles from '../component.styles';

const ToastAlert = ({
  autoClose,
  autoCloseTimeout,
  dataTest,
  i18n: i18nUser,
  isDismissable,
  isVisible,
  left,
  onDismiss,
  position,
  status,
  top: topUser,
  triggerRef
}) => {
  const i18n = {
    ...ToastAlert.defaultProps.i18n,
    ...i18nUser
  };
  const [top, setTop] = useState(topUser);
  useEffect(() => {
    // TODO: This is specific to the topbar implementation. It works but it should be more agnostic
    function changeTop() {
      const box = document
        .querySelector("header[role='banner'] > div")
        ?.getBoundingClientRect();
      if (box?.height > topUser) {
        setTop(box.height);
      }
    }

    changeTop();
    window.addEventListener('resize', changeTop);
    return () => window.removeEventListener('resize', changeTop);
  }, []);

  const valueTop = Number(top) ? `${top}px` : top;
  const valueLeft = Number(left) ? `${left}px` : left;

  const titles = {
    positive: 'Positive',
    information: 'Information',
    warning: 'Warning',
    negative: 'Negative',
    success: 'Success',
    error: 'Error',
    info: 'Information',
    neutral: 'Neutral'
  };
  const imageTitle = i18n.iconTitle || titles[status] || undefined;

  return isVisible ? (
    <Alert
      alertType={status === 'info' ? 'information' : status}
      autoClose={autoClose}
      autoCloseTimeout={autoCloseTimeout}
      css={theme => styles(theme, valueTop, valueLeft).toast}
      data-test={`${dataTest}-toast-cybsAlert`}
      onBeforeClose={onDismiss}
      position={position}
      show={isVisible}
      triggerRef={triggerRef}
    >
      <StructFlex
        direction="row"
        justify-content="space-between"
        css={theme => styles(theme, valueTop).containerClass}
      >
        <AlertIcon>
          <Icon name={status} resolution="low" title={imageTitle} />
        </AlertIcon>
        <div css={theme => styles(theme).flex}>
          <AlertLabel>
            <span role="alert">
              {imageTitle && `${imageTitle}: `}
              {i18n.message}
            </span>
          </AlertLabel>
        </div>
        {isDismissable && (
          <CybsButtonGroup
            direction="right"
            dataTest={dataTest}
            css={theme => styles(theme).closeButton}
          >
            {isVisible && (
              <CybsButton
                colorScheme="tertiary"
                dataTest={dataTest}
                iconClass="close"
                iconType="light-tiny"
                onClick={onDismiss}
                i18n={{
                  ariaLabel: i18n.ariaLabel,
                  tooltipLabel: i18n.ariaLabel
                }}
                resolution="tiny"
                variant="icon"
                tooltipCustomCSS={theme => styles(theme).tooltip}
              />
            )}
          </CybsButtonGroup>
        )}
      </StructFlex>
    </Alert>
  ) : null;
};

ToastAlert.propTypes = {
  /**
   * If true, the toast automatically closes after timeout.
   */
  autoClose: PropTypes.bool,

  /**
   * Time in milliseconds after which the toast can be dismissed.
   */

  autoCloseTimeout: PropTypes.number,

  /**
   * Data Test.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * i18n dictionary.
   */
  i18n: PropTypes.shape({
    /**
     *Aria label for the close button.
     */
    ariaLabel: PropTypes.string,
    /**
     * Message for the toast.
     */
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    /**
     * Title to display in the alert
     */
    title: PropTypes.string,
    /**
     * Title to display in the icon
     */
    iconTitle: PropTypes.string
  }),

  /**
   * If true, the toast is dismissable.
   */
  isDismissable: PropTypes.bool,

  /**
   * If true, the toast is visible.
   */
  isVisible: PropTypes.bool,

  /**
   * Margin left to apply to the toast alert.
   */
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   * Callback fired to close the alert.
   */
  onDismiss: PropTypes.func,

  /**
   * Position of the Toast
   */
  position: PropTypes.oneOf(['absolute', 'relative']),

  /**
   * Alert Type
   */
  status: PropTypes.oneOf([
    'positive',
    'information',
    'warning',
    'negative',
    'success',
    'error',
    'info',
    'neutral'
  ]),

  /**
   * Margin top to apply to the toast alert.
   */
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   *  Ref of button that opened the Alert for focus back when Alert closes
   */
  triggerRef: PropTypes.element
};

ToastAlert.defaultProps = {
  autoClose: false,
  autoCloseTimeout: 5000,
  i18n: {
    ariaLabel: 'close button',
    message: '',
    title: '',
    iconTitle: undefined
  },
  isDismissable: true,
  isVisible: false,
  onDismiss: null,
  position: 'absolute',
  status: 'information',
  triggerRef: null
};

ToastAlert.displayName = 'ToastAlert';

export default ToastAlert;
